// PATH: src/components/settings/InstructionChain.tsx
import * as React from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import type { SettingOrigin } from "../../models/domain";
import { StatusPill } from "../ui";
import { originLabel } from "../trials/trialLogic";
import BasePromptPanel from "./BasePromptPanel";
import type { RolePrompts } from "./rolePrompts";

const textSx = {
  p: 1.25,
  borderRadius: 1.5,
  bgcolor: "action.hover",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
} as const;

const Step: React.FC<{ n: number; title: string; aside?: React.ReactNode; children: React.ReactNode }> = ({ n, title, aside, children }) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
      <Typography variant="subtitle2">
        {n}. {title}
      </Typography>
      {aside}
    </Stack>
    {children}
  </Box>
);

/** Une consigne en lecture seule, ou son absence dite en clair. */
const InstructionText: React.FC<{ text?: string; empty: string; struck?: boolean }> = ({ text, empty, struck }) =>
  text ? (
    <Typography
      variant="body2"
      dir="auto"
      sx={{ ...textSx, color: struck ? "text.disabled" : undefined, textDecoration: struck ? "line-through" : undefined }}
    >
      {text}
    </Typography>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {empty}
    </Typography>
  );

interface ChainProps {
  prompts: RolePrompts;
  /** La consigne effective de la matière (globale ou de la matière), celle qu'enverrait un appel sans essai. */
  inherited?: string;
  inheritedOrigin?: SettingOrigin;
  /** Vrai dès que l'essai porte sa propre consigne : l'héritée ne part plus. */
  replaced: boolean;
  /** Le champ « consigne de l'essai ». */
  children: React.ReactNode;
}

/**
 * Ce que reçoit un agent qu'un essai surcharge, dans l'ordre d'envoi au
 * modèle : le prompt de base (lecture seule), puis UNE consigne en fin de
 * message — celle de l'essai si elle est remplie, sinon la consigne héritée.
 * Les deux ne s'additionnent jamais (même règle que le serveur).
 */
export const InstructionChain: React.FC<ChainProps> = ({ prompts, inherited, inheritedOrigin, replaced, children }) => (
  <Stack spacing={1.5}>
    <Step n={1} title="Prompt de base">
      <BasePromptPanel
        version={prompts.version}
        assembly={prompts.assembly}
        groups={prompts.groups}
        instructionsHeader={prompts.instructionsHeader}
        instructionsNote="la consigne de l'essai si elle est remplie, sinon la consigne héritée."
        error={prompts.error}
      />
    </Step>
    <Step
      n={2}
      title="Consigne héritée"
      aside={
        <>
          {inherited && <StatusPill tone={inheritedOrigin === "subject" ? "info" : "neutral"}>{originLabel(inheritedOrigin)}</StatusPill>}
          {inherited && replaced && <Chip size="small" color="warning" variant="outlined" label="remplacée par celle de l'essai" />}
        </>
      }
    >
      <InstructionText text={inherited} empty="Aucune : ni la matière ni le global n'en ont." struck={replaced} />
    </Step>
    <Step n={3} title="Consigne de l'essai">
      {children}
    </Step>
  </Stack>
);

interface SentProps {
  prompts: RolePrompts;
  /** Les versions du prompt de base relevées sur ce que l'essai a produit ; vide = non enregistrée. */
  used: string[];
  /** La consigne réellement envoyée, et d'où elle venait. */
  instructions?: string;
  origin?: SettingOrigin;
}

/**
 * Ce qu'un essai a réellement envoyé à un agent : la version du prompt de
 * base, puis la consigne utilisée et son origine. Le texte du prompt affiché
 * est celui d'AUJOURD'HUI : on le dit quand la version a changé depuis.
 */
export const SentInstructions: React.FC<SentProps> = ({ prompts, used, instructions, origin }) => {
  const changed = Boolean(prompts.version) && used.length > 0 && used.some((v) => v !== prompts.version);
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2">Prompt de base :</Typography>
        {used.length ? (
          used.map((v) => <Chip key={v} size="small" label={v} />)
        ) : (
          <Typography variant="body2" color="text.secondary">
            version non enregistrée par l'essai{prompts.version ? ` (aujourd'hui ${prompts.version})` : ""}
          </Typography>
        )}
      </Stack>
      {changed && (
        <Alert severity="info" sx={{ py: 0 }}>
          Le prompt de base a changé depuis l'essai (aujourd'hui {prompts.version}) : le texte ci-dessous est l'actuel.
        </Alert>
      )}
      <BasePromptPanel
        version={prompts.version}
        assembly={prompts.assembly}
        groups={prompts.groups}
        instructionsHeader={prompts.instructionsHeader}
        instructionsNote="la consigne utilisée par l'essai, ci-dessous."
        error={prompts.error}
      />
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2">Consigne utilisée</Typography>
          {instructions && <StatusPill tone={origin === "trial" ? "primary" : origin === "subject" ? "info" : "neutral"}>{originLabel(origin)}</StatusPill>}
        </Stack>
        <InstructionText text={instructions} empty="Aucune : le prompt de base seul." />
      </Box>
    </Stack>
  );
};

export default InstructionChain;
